// ============================================
// Toast Notification System
// ============================================

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// Optional action button rendered inside the toast (e.g. "Undo" after a
// skip). onAction fires once on tap and the toast dismisses immediately;
// errors are the action's own responsibility (fire a follow-up toast).
interface ToastAction {
  label: string;
  onAction: () => void | Promise<void>;
}

interface ToastOptions {
  action?: ToastAction;
  /** Override auto-dismiss. Defaults: 4000ms, or 5000ms when an action
      is present so the user has time to reach for it. */
  durationMs?: number;
}

interface ToastItem {
  id: number;
  message: string;
  type: 'error' | 'success';
  action?: ToastAction;
}

interface ToastContextType {
  showToast: (message: string, type?: 'error' | 'success', options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType>({ showToast: () => {} });

export const useToast = () => useContext(ToastContext);

let toastId = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback((message: string, type: 'error' | 'success' = 'error', options?: ToastOptions) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type, action: options?.action }]);
    const duration = options?.durationMs ?? (options?.action ? 5000 : 4000);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  // Stable context value - showToast is already a stable useCallback, so
  // the memo deps never change after mount and consumers avoid re-rendering.
  const contextValue = useMemo(() => ({ showToast }), [showToast]);

  const dismiss = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id));

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {toasts.length > 0 && (
        <div className="toast-container" role="status" aria-live="polite">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`toast toast-${toast.type}${toast.action ? ' toast-with-action' : ''} animate-slide-down`}
              onClick={() => dismiss(toast.id)}
            >
              <span className="toast-message">{toast.message}</span>
              {toast.action && (
                <button
                  className="toast-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    void toast.action!.onAction();
                    dismiss(toast.id);
                  }}
                >
                  {toast.action.label}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};
